import { useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi, errMsg, imgUrl } from "@/lib/api";
import { Btn } from "@/admin/ui";

const Field = ({ label, children }) => <div className="space-y-1"><Label className="text-xs font-bold uppercase text-stone-500">{label}</Label>{children}</div>;

const ComboEditor = ({ f, setF, products, comboPrice }) => {
  const items = f.combo_items || [];
  const setItem = (i, k, v) => setF({ ...f, combo_items: items.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  return (
    <div className="rounded-md border border-stone-200 p-3 space-y-2" data-testid="combo-editor">
      <p className="text-xs font-bold uppercase text-stone-500">Combo contents (shown to customers as “Includes”)</p>
      {items.map((c, i) => (
        <div key={i} className="flex gap-2 items-center">
          <select value={c.product_id} onChange={(e) => setItem(i, "product_id", e.target.value)} className="h-9 flex-1 rounded-md border border-stone-300 px-2 text-sm" data-testid={`combo-item-select-${i}`}>
            <option value="">Choose item…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit}){p.price > 0 ? ` · ₹${p.price}` : ""}</option>)}
          </select>
          <span className="text-xs text-stone-500">×</span>
          <Input type="number" step="0.25" min="0" value={c.factor} onChange={(e) => setItem(i, "factor", e.target.value)} className="h-9 w-20" data-testid={`combo-item-factor-${i}`} />
          <button type="button" onClick={() => setF({ ...f, combo_items: items.filter((_, j) => j !== i) })} className="p-1.5 text-stone-500 hover:text-accent" data-testid={`combo-item-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <Btn type="button" variant="outline" onClick={() => setF({ ...f, combo_items: [...items, { product_id: "", factor: 1 }] })} data-testid="combo-add-item-btn">+ Add item</Btn>
      <label className="flex items-center gap-2 text-sm font-semibold pt-1">
        <Switch checked={!!f.combo_auto} onCheckedChange={(v) => setF({ ...f, combo_auto: v })} data-testid="combo-auto-switch" /> Auto price = sum of items + extra
      </label>
      {f.combo_auto && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-stone-500">Extra ₹ (use minus for a discount)</span>
          <Input type="number" value={f.combo_charge} onChange={(e) => setF({ ...f, combo_charge: e.target.value })} className="h-9 w-24" data-testid="combo-charge-input" />
          <span data-testid="combo-price-preview">{comboPrice !== null ? <>= <b className="text-primary">₹{comboPrice}</b></> : "(all items need a price)"}</span>
        </div>
      )}
    </div>
  );
};

const PriceLinkEditor = ({ link, onChange, products, base, linkedPrice }) => (
  <div className="rounded-md border border-stone-200 p-3 space-y-2" data-testid="price-link-editor">
    <label className="flex items-center gap-2 text-sm font-semibold">
      <Switch checked={!!link} onCheckedChange={(v) => onChange(v ? { base_id: "", factor: 0.25, charge: 0 } : null)} data-testid="price-link-switch" />
      Auto price from vegetable's daily price
    </label>
    {link && (
      <div className="grid grid-cols-3 gap-2">
        <select value={link.base_id} onChange={(e) => onChange({ ...link, base_id: e.target.value })} className="col-span-3 h-9 rounded-md border border-stone-300 px-2 text-sm" data-testid="price-link-base-select">
          <option value="">Choose vegetable…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit}) · ₹{p.price}</option>)}
        </select>
        <Field label="Pack ÷ base unit"><Input type="number" step="0.05" value={link.factor} onChange={(e) => onChange({ ...link, factor: e.target.value })} className="h-9" data-testid="price-link-factor" /></Field>
        <Field label="Cutting charge ₹"><Input type="number" min="0" value={link.charge} onChange={(e) => onChange({ ...link, charge: e.target.value })} className="h-9" data-testid="price-link-charge" /></Field>
        <div className="text-xs text-stone-500 self-end pb-2" data-testid="price-link-preview">
          {base ? <>₹{base.price} × {link.factor} + ₹{link.charge || 0} = <b className="text-primary">₹{linkedPrice}</b></> : "Pick a vegetable"}
        </div>
        <p className="col-span-3 text-[11px] text-stone-500">e.g. 250 g pack from a 1 kg vegetable = 0.25. Price updates automatically whenever the vegetable price changes.</p>
      </div>
    )}
  </div>
);

const CutOptionsEditor = ({ value, onChange }) => {
  const set = (i, k, v) => onChange(value.map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  const has = (l) => value.some((o) => o.label.toLowerCase() === l.toLowerCase());
  return (
    <div className="space-y-2" data-testid="cut-options-editor">
      {value.map((o, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input value={o.label} onChange={(e) => set(i, "label", e.target.value)} placeholder="e.g. Cut" className="h-9" data-testid={`cut-label-${i}`} />
          <span className="text-sm text-stone-500">+₹</span>
          <Input type="number" min="0" value={o.charge} onChange={(e) => set(i, "charge", e.target.value)} className="h-9 w-24" data-testid={`cut-charge-${i}`} />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-1.5 text-stone-500 hover:text-accent" data-testid={`cut-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        {["Cut", "Chopped", "Peeled"].filter((l) => !has(l)).map((l) => (
          <Btn key={l} type="button" variant="outline" onClick={() => onChange([...value, { label: l, charge: "" }])} data-testid={`cut-add-${l.toLowerCase()}`}>+ {l}</Btn>
        ))}
        <Btn type="button" variant="ghost" onClick={() => onChange([...value, { label: "", charge: "" }])} data-testid="cut-add-custom">+ Custom</Btn>
      </div>
    </div>
  );
};

export const ImageField = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { onChange((await adminApi.post("/upload", fd)).data.url); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={imgUrl(value)} alt="" className="w-14 h-14 rounded object-cover border" /> : <div className="w-14 h-14 rounded bg-stone-100" />}
      <label className="flex items-center gap-2 text-sm font-semibold text-primary cursor-pointer border border-stone-300 rounded-md px-3 h-9" data-testid={`${testId}-upload`}>
        <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload photo"}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
    </div>
  );
};

export const ProductDialog = ({ product, cats, products = [], onClose, onSaved }) => {
  const [f, setF] = useState({ name: "", category_id: cats[0]?.id || "", unit: "", price: "", stock_qty: "", low_stock_threshold: 5, active: true, image: "", cut_options: [], price_link: null, needs_prep: false, combo_items: [], combo_charge: 0, combo_auto: false, ...product });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const link = f.price_link;
  const base = link && products.find((p) => p.id === link.base_id);
  const linkedPrice = base ? Math.round(base.price * (Number(link.factor) || 0) + (Number(link.charge) || 0)) : null;
  const comboParts = (f.combo_items || []).map((c) => ({ ...c, p: products.find((x) => x.id === c.product_id) }));
  const comboPrice = f.combo_auto && comboParts.length && comboParts.every((c) => c.p?.price > 0)
    ? Math.max(1, Math.round(comboParts.reduce((s, c) => s + c.p.price * (Number(c.factor) || 0), 0) + (Number(f.combo_charge) || 0))) : null;
  const save = async () => {
    const price_link = link && link.base_id ? { base_id: link.base_id, factor: Number(link.factor), charge: Number(link.charge) || 0 } : null;
    const body = { name: f.name, category_id: f.category_id, unit: f.unit, price: price_link ? linkedPrice : comboPrice ?? Number(f.price), stock_qty: parseInt(f.stock_qty, 10), low_stock_threshold: parseInt(f.low_stock_threshold, 10) || 0, active: f.active, image: f.image, price_link, needs_prep: !!f.needs_prep,
      combo_items: (f.combo_items || []).filter((c) => c.product_id).map((c) => ({ product_id: c.product_id, factor: Number(c.factor) || 1 })),
      combo_charge: Number(f.combo_charge) || 0, combo_auto: !!f.combo_auto && (f.combo_items || []).length > 0,
      cut_options: (f.cut_options || []).filter((o) => o.label.trim()).map((o) => ({ label: o.label.trim(), charge: Number(o.charge) || 0 })) };
    try {
      product.id ? await adminApi.put(`/products/${product.id}`, body) : await adminApi.post("/products", body);
      toast.success("Product saved"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-md max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader><DialogTitle>{product.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Name"><Input value={f.name} onChange={set("name")} data-testid="product-name-input" /></Field></div>
          <Field label="Category">
            <select value={f.category_id} onChange={set("category_id")} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="product-category-select">
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Unit"><Input value={f.unit} onChange={set("unit")} placeholder="500 g / 1 kg / 1 bunch" data-testid="product-unit-input" /></Field>
          <Field label="Price ₹"><Input type="number" value={link ? linkedPrice ?? "" : comboPrice ?? f.price} disabled={!!link || comboPrice !== null} onChange={set("price")} data-testid="product-price-input" /></Field>
          <Field label="Stock qty"><Input type="number" value={f.stock_qty} onChange={set("stock_qty")} data-testid="product-stock-input" /></Field>
          <Field label="Low stock at"><Input type="number" value={f.low_stock_threshold} onChange={set("low_stock_threshold")} data-testid="product-threshold-input" /></Field>
          <div className="col-span-2"><PriceLinkEditor link={link} onChange={(v) => setF({ ...f, price_link: v })} products={products.filter((p) => p.id !== product.id && !p.price_link && p.price > 0)} base={base} linkedPrice={linkedPrice} /></div>
          {(f.category_id === "combos" || (f.combo_items || []).length > 0) && (
            <div className="col-span-2"><ComboEditor f={f} setF={setF} products={products.filter((p) => p.id !== product.id && p.category_id !== "combos")} comboPrice={comboPrice} /></div>
          )}
          <label className="col-span-2 flex items-center gap-2 text-sm font-semibold"><Switch checked={!!f.needs_prep} onCheckedChange={(v) => setF({ ...f, needs_prep: v })} data-testid="product-prep-switch" /> Needs cutting prep time (only later slots)</label>
          <div className="col-span-2"><Field label="Photo"><ImageField value={f.image} onChange={(v) => setF({ ...f, image: v })} testId="product-image" /></Field></div>
          <div className="col-span-2"><Field label="Cutting options (customer can choose; extra charge per unit)"><CutOptionsEditor value={f.cut_options || []} onChange={(v) => setF({ ...f, cut_options: v })} /></Field></div>
          <label className="col-span-2 flex items-center gap-2 text-sm font-semibold"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="product-active-switch" /> Live on storefront</label>
        </div>
        <div className="flex justify-end gap-2 mt-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="product-save-btn">Save</Btn></div>
      </DialogContent>
    </Dialog>
  );
};

export const CategoryDialog = ({ category, onClose, onSaved }) => {
  const [f, setF] = useState({ name: "", image: "", sort: 0, ...category });
  const save = async () => {
    const body = { name: f.name, image: f.image, sort: parseInt(f.sort, 10) || 0 };
    try {
      category.id ? await adminApi.put(`/categories/${category.id}`, body) : await adminApi.post("/categories", body);
      toast.success("Category saved"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-md" data-testid="category-dialog">
        <DialogHeader><DialogTitle>{category.id ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="category-name-input" /></Field>
          <Field label="Sort order"><Input type="number" value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} data-testid="category-sort-input" /></Field>
          <Field label="Photo"><ImageField value={f.image} onChange={(v) => setF({ ...f, image: v })} testId="category-image" /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="category-save-btn">Save</Btn></div>
      </DialogContent>
    </Dialog>
  );
};
